import { useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import {
  DEFAULT_STYLE,
  DRAWING_LIMITS,
  arrowHeadPath,
  constrainBox,
  constrainLine,
  drawingBounds,
  drawingDistance,
  drawingPath,
  fillable,
  simplifyStroke,
  type DrawingInput,
  type DrawingKind,
  type DrawingStyle,
  type MapDrawing,
  type MapLayer,
  type MapPoint,
} from '@realmery/realm-core';
import { realms } from '../db/client.ts';
import { settings } from '../settings.ts';
import { ColorField } from './ColorField.tsx';

/**
 * The Atlas's Draw tool (docs/PARITY-STEP-4-PLAN.md section 7), as the desktop's
 * `AtlasDrawTool`: layers (a first "Drawings" layer is made for the first drawing), the
 * shapes (pen, line, arrow, box, ellipse, text), the style (colour, thickness, dashes, a
 * fill, a text size), Select to pick, move, restyle or delete a drawing, and Undo and Redo
 * of drawing changes. Drawings are in the map's own pixels, so they zoom with it, like ink.
 */

export type DrawMode = 'select' | 'pen' | 'line' | 'arrow' | 'box' | 'ellipse' | 'text';

const MODES: readonly { mode: DrawMode; label: string; kind: DrawingKind | null; hint: string }[] = [
  { mode: 'select', label: '↖ Select', kind: null, hint: 'Click a drawing to select it · drag to move · Delete removes · restyle it from the panel' },
  { mode: 'pen', label: '✎ Pen', kind: 'PEN', hint: 'Drag to draw freehand · Ctrl+Z undoes' },
  { mode: 'line', label: '╱ Line', kind: 'LINE', hint: 'Drag for a straight line, or click point by point and double-click (or Enter) to finish · Shift keeps 45°' },
  { mode: 'arrow', label: '↗ Arrow', kind: 'ARROW', hint: 'Drag for a straight arrow, or click point by point and double-click (or Enter) to finish · Shift keeps 45°' },
  { mode: 'box', label: '▭ Box', kind: 'RECTANGLE', hint: 'Drag out the box · Shift makes it square' },
  { mode: 'ellipse', label: '◯ Ellipse', kind: 'ELLIPSE', hint: 'Drag out the ellipse · Shift makes it a circle' },
  { mode: 'text', label: 'T Text', kind: 'TEXT', hint: 'Click where the text should go' },
];

/** `AtlasDrawTool.SWATCHES`. */
const DRAW_SWATCHES = [
  { hex: '#C0392B', name: 'Red' },
  { hex: '#E67E22', name: 'Orange' },
  { hex: '#F1C40F', name: 'Yellow' },
  { hex: '#27AE60', name: 'Green' },
  { hex: '#2E86C1', name: 'Blue' },
  { hex: '#8E44AD', name: 'Purple' },
  { hex: '#1C1C1C', name: 'Black' },
  { hex: '#FFFFFF', name: 'White' },
];
const UNDO_LIMIT = 50;
/** How close, in screen pixels, a click must be to pick a drawing (`PICK_PX`). */
const PICK = 7;
const DRAG = 3;

const inputOf = (d: MapDrawing): DrawingInput => ({
  id: d.id,
  layerId: d.layerId,
  kind: d.kind,
  points: d.points,
  text: d.text,
  strokeColor: d.strokeColor,
  strokeWidth: d.strokeWidth,
  dashed: d.dashed,
  fillColor: d.fillColor,
  fillOpacity: d.fillOpacity,
  textSize: d.textSize,
});

/** A drawing, in map pixels: its stroke and fill, an arrow's head, or text with a pale halo (`AnnotationArt`). */
export function DrawingArt({ drawing: d, offset }: { drawing: Pick<MapDrawing, 'kind' | 'points' | 'strokeColor' | 'strokeWidth' | 'dashed' | 'fillColor' | 'fillOpacity' | 'text' | 'textSize'>; offset?: MapPoint | undefined }) {
  const moved = offset ? { ...d, points: d.points.map((p) => ({ x: p.x + offset.x, y: p.y + offset.y })) } : d;
  if (d.kind === 'TEXT') {
    const at = moved.points[0] ?? { x: 0, y: 0 };
    return (
      <text
        x={at.x}
        y={at.y}
        dominantBaseline="text-before-edge"
        fontFamily="Georgia, serif"
        fontWeight="bold"
        fontSize={d.textSize}
        fill={d.strokeColor}
        stroke="rgb(255 250 240 / 90%)"
        strokeWidth={2 * Math.max(2, d.textSize / 7)}
        strokeLinejoin="round"
        paintOrder="stroke"
        className="atlas-drawing"
      >
        {d.text}
      </text>
    );
  }
  const head = arrowHeadPath(moved);
  return (
    <g className="atlas-drawing">
      <path
        d={drawingPath(moved)}
        stroke={d.strokeColor}
        strokeWidth={d.strokeWidth}
        strokeLinecap={d.dashed ? 'butt' : 'round'}
        strokeLinejoin="round"
        strokeDasharray={d.dashed ? `${d.strokeWidth * 3} ${d.strokeWidth * 2}` : undefined}
        fill={fillable(d.kind) && d.fillColor ? d.fillColor : 'none'}
        fillOpacity={d.fillOpacity}
      />
      {head && <path d={head} fill={d.strokeColor} stroke={d.strokeColor} strokeWidth={Math.max(1, d.strokeWidth / 3)} strokeLinejoin="round" />}
    </g>
  );
}

export interface DrawTool {
  readonly hint: string;
  /** Whether the pointer press was the Draw tool's (else the map pans). */
  pointerDown(event: PointerEvent<SVGSVGElement>, at: MapPoint): boolean;
  pointerMove(event: PointerEvent<SVGSVGElement>, at: MapPoint): boolean;
  pointerUp(event: PointerEvent<SVGSVGElement>, at: MapPoint): boolean;
  doubleClick(at: MapPoint): void;
  /** Whether the key was the Draw tool's. */
  key(event: KeyboardEvent): boolean;
  /** Drawn in the map's own pixels, over the regions. */
  readonly art: ReactNode;
  readonly panel: ReactNode;
  readonly hiddenLayers: ReadonlySet<string>;
  setLayerShown(id: string, shown: boolean): void;
}

export function useDrawTool({
  file,
  mapId,
  layers,
  drawings,
  viewScale,
  run,
  active,
}: {
  file: string;
  mapId: string;
  layers: readonly MapLayer[];
  drawings: readonly MapDrawing[];
  viewScale: number;
  run: (work: () => Promise<unknown>, fallback: string) => Promise<boolean>;
  active: boolean;
}): DrawTool {
  const [mode, setMode] = useState<DrawMode>('pen');
  const [style, setStyle] = useState<DrawingStyle>(DEFAULT_STYLE);
  const [fillOn, setFillOn] = useState(false);
  const [layerChoice, setLayerChoice] = useState<string | null>(null);
  const [draft, setDraft] = useState<readonly MapPoint[]>([]);
  const [clicking, setClicking] = useState(false);
  const [hover, setHover] = useState<MapPoint | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [press, setPress] = useState<{ at: MapPoint; moved: boolean; pointer: number } | null>(null);
  const [offset, setOffset] = useState<MapPoint | null>(null);
  const [textAt, setTextAt] = useState<MapPoint | null>(null);
  const [words, setWords] = useState('');
  const [editingText, setEditingText] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deletingLayer, setDeletingLayer] = useState(false);
  /** A layer's Players can see while its change is being saved. */
  const [seen, setSeen] = useState<Readonly<Record<string, boolean>>>({});
  const [layerName, setLayerName] = useState('');
  const [undo, setUndo] = useState<readonly (readonly DrawingInput[])[]>([]);
  const [redo, setRedo] = useState<readonly (readonly DrawingInput[])[]>([]);
  const [hiddenLayers, setHiddenLayers] = useState<ReadonlySet<string>>(new Set());

  // The chosen layer, else the top one; none until the first drawing makes one.
  const layer = layers.find((l) => l.id === layerChoice) ?? layers.at(-1) ?? null;
  const current = drawings.map(inputOf);
  const picked = selected ? drawings.find((d) => d.id === selected) : undefined;
  const kind = MODES.find((m) => m.mode === mode)!.kind;

  /** Runs a drawing change, remembering the drawings as they were for Undo. */
  async function change(work: () => Promise<unknown>, fallback: string) {
    const before = current;
    if (await run(work, fallback)) {
      setUndo((u) => [before, ...u].slice(0, UNDO_LIMIT));
      setRedo([]);
    }
  }

  async function layerForNewDrawing(): Promise<string> {
    if (layer) return layer.id;
    const { id } = await realms.createLayer(file, mapId, 'Drawings');
    setLayerChoice(id);
    return id;
  }

  const styleFor = (k: DrawingKind): DrawingStyle => ({ ...style, fillColor: fillable(k) && fillOn ? (style.fillColor ?? style.strokeColor) : null });

  function add(k: DrawingKind, points: readonly MapPoint[], text = '') {
    const id = crypto.randomUUID();
    void change(async () => {
      const layerId = await layerForNewDrawing();
      await realms.saveDrawing(file, mapId, { ...styleFor(k), id, layerId, kind: k, points: points.map(round), text });
    }, 'The drawing could not be saved.');
  }

  /** Restyles the selected drawing from the panel, as the desktop does. */
  function restyle(next: DrawingStyle, nextFill = fillOn) {
    setStyle(next);
    setFillOn(nextFill);
    if (!picked) return;
    const filled = fillable(picked.kind) && nextFill ? (next.fillColor ?? next.strokeColor) : null;
    void change(() => realms.saveDrawing(file, mapId, { ...inputOf(picked), ...next, fillColor: filled }), 'The drawing could not be restyled.');
  }

  function pick(at: MapPoint): MapDrawing | undefined {
    let best: MapDrawing | undefined;
    let bestDistance = PICK / viewScale;
    // The top drawing wins a tie, as it's drawn over the others.
    for (const d of [...drawings].reverse()) {
      if (hiddenLayers.has(d.layerId)) continue;
      const distance = drawingDistance(d, at);
      if (distance <= bestDistance) {
        best = d;
        bestDistance = distance;
      }
    }
    return best;
  }

  function select(d: MapDrawing | undefined) {
    setSelected(d?.id ?? null);
    setEditingText(false);
    if (d) {
      setStyle({ strokeColor: d.strokeColor, strokeWidth: d.strokeWidth, dashed: d.dashed, fillColor: d.fillColor ?? style.fillColor, fillOpacity: d.fillOpacity, textSize: d.textSize });
      setFillOn(d.fillColor !== null);
    }
  }

  function finishClicks() {
    if (clicking && draft.length >= 2 && kind) add(kind, draft);
    setClicking(false);
    setDraft([]);
  }

  function cancel(): boolean {
    if (clicking || draft.length > 0 || textAt) {
      setClicking(false);
      setDraft([]);
      setTextAt(null);
      return true;
    }
    if (selected) {
      select(undefined);
      return true;
    }
    return false;
  }

  const constrain = (from: MapPoint, to: MapPoint, shift: boolean) =>
    !shift ? to : mode === 'box' || mode === 'ellipse' ? constrainBox(from, to) : mode === 'line' || mode === 'arrow' ? constrainLine(from, to) : to;

  async function undoOnce() {
    const [last, ...rest] = undo;
    if (!last) return;
    const now = current;
    if (await run(() => realms.restoreDrawings(file, mapId, last), 'That could not be undone.')) {
      setUndo(rest);
      setRedo((r) => [now, ...r]);
      setSelected(null);
    }
  }

  async function redoOnce() {
    const [next, ...rest] = redo;
    if (!next) return;
    const now = current;
    if (await run(() => realms.restoreDrawings(file, mapId, next), 'That could not be redone.')) {
      setRedo(rest);
      setUndo((u) => [now, ...u].slice(0, UNDO_LIMIT));
      setSelected(null);
    }
  }

  /** Deletes a layer and its drawings; Undo's history goes with it, as on the desktop. */
  function deleteLayer(id: string) {
    void run(() => realms.deleteLayer(file, id), 'The layer could not be deleted.').then(() => {
      setUndo([]);
      setRedo([]);
      setLayerChoice(null);
      setDeletingLayer(false);
    });
  }

  const deleteSelected = () => {
    if (!picked) return;
    void change(() => realms.deleteDrawing(file, mapId, picked.id), 'The drawing could not be deleted.');
    setSelected(null);
  };

  const tool: DrawTool = {
    hint: MODES.find((m) => m.mode === mode)!.hint,
    hiddenLayers,
    setLayerShown(id, shown) {
      const next = new Set(hiddenLayers);
      if (shown) next.delete(id);
      else next.add(id);
      setHiddenLayers(next);
    },

    pointerDown(event, at) {
      if (!active || event.button !== 0 || !event.isPrimary) return false;
      if (mode === 'select') {
        const hit = pick(at);
        select(hit);
        if (!hit) return false;
        event.currentTarget.setPointerCapture(event.pointerId);
        setPress({ at, moved: false, pointer: event.pointerId });
        return true;
      }
      if (mode === 'text') return true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setPress({ at, moved: false, pointer: event.pointerId });
      if (!clicking) setDraft([at]);
      return true;
    },

    pointerMove(event, at) {
      if (!active) return false;
      setHover(at);
      if (!press || press.pointer !== event.pointerId) return clicking;
      const far = Math.hypot(at.x - press.at.x, at.y - press.at.y) * viewScale > DRAG;
      if (!press.moved && !far) return true;
      if (!press.moved) setPress({ ...press, moved: true });
      if (mode === 'select') {
        setOffset({ x: at.x - press.at.x, y: at.y - press.at.y });
      } else if (mode === 'pen') {
        const last = draft.at(-1);
        if (!last || Math.hypot(at.x - last.x, at.y - last.y) * viewScale >= 2) setDraft([...draft, at]);
      } else if (!clicking) {
        setDraft([draft[0] ?? press.at, constrain(draft[0] ?? press.at, at, event.shiftKey)]);
      }
      return true;
    },

    pointerUp(event, at) {
      if (!active) return false;
      const p = press;
      setPress(null);
      if (mode === 'text') {
        setTextAt(at);
        setWords('');
        return true;
      }
      if (!p || p.pointer !== event.pointerId) return false;
      if (mode === 'select') {
        if (p.moved && picked) {
          const dx = at.x - p.at.x;
          const dy = at.y - p.at.y;
          void change(() => realms.saveDrawing(file, mapId, { ...inputOf(picked), points: picked.points.map((q) => round({ x: q.x + dx, y: q.y + dy })) }), 'The drawing could not be moved.');
        }
        setOffset(null);
        return true;
      }
      if ((mode === 'line' || mode === 'arrow') && (clicking || !p.moved)) {
        // Click by click: each click adds a corner; a double-click (or Enter) finishes.
        if (!clicking) {
          setClicking(true);
          setDraft([at]);
        } else {
          setDraft([...draft, constrain(draft.at(-1)!, at, event.shiftKey)]);
        }
        return true;
      }
      if (!p.moved) {
        setDraft([]);
        return true;
      }
      let points = draft;
      setDraft([]);
      if (mode === 'pen') {
        points = simplifyStroke([...draft, at], 1.2 / viewScale);
        if (points.length < 2) return true;
      }
      if (kind) add(kind, points);
      return true;
    },

    doubleClick(at) {
      if (!active) return;
      if (mode === 'select') {
        const hit = pick(at);
        if (hit?.kind === 'TEXT') {
          select(hit);
          setWords(hit.text);
          setEditingText(true);
        }
        return;
      }
      if (clicking) {
        // The double-click's own clicks each added a point where it was: one is enough.
        const points = draft.filter((q, i) => i === 0 || Math.hypot(q.x - draft[i - 1]!.x, q.y - draft[i - 1]!.y) * viewScale > 3);
        setClicking(false);
        setDraft([]);
        if (points.length >= 2 && kind) add(kind, points);
      }
    },

    key(event) {
      if (!active) return false;
      const ctrl = event.ctrlKey || event.metaKey;
      if (ctrl && event.key.toLowerCase() === 'z' && !event.shiftKey) {
        event.preventDefault();
        void undoOnce();
        return true;
      }
      if (ctrl && (event.key.toLowerCase() === 'y' || (event.key.toLowerCase() === 'z' && event.shiftKey))) {
        event.preventDefault();
        void redoOnce();
        return true;
      }
      if (ctrl || event.altKey) return false;
      if (event.key === 'Enter' && clicking) {
        event.preventDefault();
        finishClicks();
        return true;
      }
      if (event.key === 'Backspace' && clicking) {
        event.preventDefault();
        setDraft(draft.slice(0, -1));
        return true;
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && picked) {
        event.preventDefault();
        deleteSelected();
        return true;
      }
      if (event.key === 'Escape') return cancel();
      return false;
    },

    art: (
      <>
        {drawings
          .filter((d) => !hiddenLayers.has(d.layerId))
          .map((d) => (
            <DrawingArt key={d.id} drawing={d} offset={d.id === selected && offset ? offset : undefined} />
          ))}
        {active && picked && <SelectionBox drawing={picked} offset={offset} />}
        {active && draft.length > 0 && kind && kind !== 'TEXT' && (
          <g opacity={0.8} pointerEvents="none">
            <DrawingArt drawing={{ ...styleFor(kind), kind, points: clicking && hover ? [...draft, hover] : draft, text: '' }} />
          </g>
        )}
        {active && textAt && <circle cx={textAt.x} cy={textAt.y} r={4 / viewScale} fill={style.strokeColor} pointerEvents="none" />}
      </>
    ),

    panel: (
      <div className="atlas-draw-panel" role="group" aria-label="Draw">
        <h2>Draw</h2>
        <div className="atlas-layer-row">
          <label>
            Layer
            <select value={layer?.id ?? ''} onChange={(e) => setLayerChoice(e.currentTarget.value)} disabled={layers.length === 0}>
              {layers.length === 0 && <option value="">Drawings (made by your first drawing)</option>}
              {[...layers].reverse().map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <span className="actions">
            <button type="button" title="A new layer, on top, hidden from players" aria-label="New layer" onClick={() => void run(async () => setLayerChoice((await realms.createLayer(file, mapId, `Layer ${layers.length + 1}`)).id), 'The layer could not be made.')}>
              +
            </button>
            <button
              type="button"
              disabled={!layer}
              onClick={() => {
                setLayerName(layer?.name ?? '');
                setRenaming(true);
              }}
            >
              Rename…
            </button>
            <button type="button" aria-label="Move the layer up" title="Up: drawn over the layers below" disabled={!layer || layer.id === layers.at(-1)?.id} onClick={() => layer && void run(() => realms.moveLayer(file, layer.id, true), 'The layer could not be moved.')}>
              ↑
            </button>
            <button type="button" aria-label="Move the layer down" title="Down: drawn under the layers above" disabled={!layer || layer.id === layers[0]?.id} onClick={() => layer && void run(() => realms.moveLayer(file, layer.id, false), 'The layer could not be moved.')}>
              ↓
            </button>
            <button
              type="button"
              className="quiet"
              disabled={!layer}
              onClick={() => {
                if (!layer) return;
                if (drawings.some((d) => d.layerId === layer.id) && settings()['general.confirmDeletes']) setDeletingLayer(true);
                else deleteLayer(layer.id);
              }}
            >
              Delete…
            </button>
          </span>
        </div>
        {deletingLayer && layer && (
          <div className="panel confirm" role="alertdialog" aria-label={`Delete ${layer.name}`}>
            <p>{`Delete “${layer.name}” and its ${plural(drawings.filter((d) => d.layerId === layer.id).length, 'drawing', 'drawings')}? Undo can't bring them back.`}</p>
            <div className="actions">
              <button type="button" className="danger" onClick={() => deleteLayer(layer.id)}>
                Delete layer
              </button>
              <button type="button" onClick={() => setDeletingLayer(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {renaming && layer && (
          <div className="atlas-inline-form" role="group" aria-label="Rename layer">
            <input aria-label="Layer name" value={layerName} maxLength={DRAWING_LIMITS.layerName} autoFocus placeholder="e.g. March to Red Hollow, Traps" onChange={(e) => setLayerName(e.currentTarget.value)} />
            <button type="button" onClick={() => void run(() => realms.updateLayer(file, layer.id, layerName, layer.playersCanSee), 'The layer could not be renamed.').then((ok) => ok && setRenaming(false))}>
              Rename
            </button>
            <button type="button" onClick={() => setRenaming(false)}>
              Cancel
            </button>
          </div>
        )}
        <label className="check">
          <input
            type="checkbox"
            checked={layer ? (seen[layer.id] ?? layer.playersCanSee) : false}
            disabled={!layer}
            onChange={(e) => {
              if (!layer) return;
              // Shown at once; the saved value takes over when it's back.
              const value = e.currentTarget.checked;
              setSeen((all) => ({ ...all, [layer.id]: value }));
              void run(() => realms.updateLayer(file, layer.id, layer.name, value), 'The layer could not be changed.').then(() =>
                setSeen((all) => {
                  const { [layer.id]: _, ...rest } = all;
                  return rest;
                }),
              );
            }}
          />{' '}
          Players can see this layer
        </label>
        <div className="view-switch atlas-draw-modes" role="group" aria-label="Shape">
          {MODES.map((m) => (
            <button
              key={m.mode}
              type="button"
              className={mode === m.mode ? 'tab selected' : 'tab'}
              aria-pressed={mode === m.mode}
              title={m.hint}
              onClick={() => {
                setMode(m.mode);
                setDraft([]);
                setClicking(false);
                setTextAt(null);
                if (m.mode !== 'select') select(undefined);
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
        <ColorField label="Colour" value={style.strokeColor} swatches={DRAW_SWATCHES} onChange={(hex) => restyle({ ...style, strokeColor: hex })} />
        <label>
          Thickness
          <input type="range" min={1} max={40} step={1} value={style.strokeWidth} onChange={(e) => restyle({ ...style, strokeWidth: Number(e.currentTarget.value) })} />
        </label>
        <label className="check">
          <input type="checkbox" checked={style.dashed} onChange={(e) => restyle({ ...style, dashed: e.currentTarget.checked })} /> Dashed
        </label>
        {((kind !== null && fillable(kind)) || (picked && fillable(picked.kind))) && (
          <>
            <label className="check">
              <input type="checkbox" checked={fillOn} onChange={(e) => restyle(style, e.currentTarget.checked)} /> Fill
            </label>
            {fillOn && (
              <>
                <ColorField label="Fill colour" value={style.fillColor ?? style.strokeColor} swatches={DRAW_SWATCHES} onChange={(hex) => restyle({ ...style, fillColor: hex })} />
                <label>
                  Fill opacity
                  <input type="range" min={0.05} max={1} step={0.05} value={style.fillOpacity} onChange={(e) => restyle({ ...style, fillOpacity: Number(e.currentTarget.value) })} />
                </label>
              </>
            )}
          </>
        )}
        {(mode === 'text' || picked?.kind === 'TEXT') && (
          <label>
            Text size
            <input type="range" min={8} max={160} step={1} value={style.textSize} onChange={(e) => restyle({ ...style, textSize: Number(e.currentTarget.value) })} />
          </label>
        )}
        {(textAt || editingText) && (
          <div className="atlas-inline-form" role="group" aria-label={editingText ? 'Edit text' : 'New text'}>
            <input
              aria-label="Text"
              value={words}
              maxLength={DRAWING_LIMITS.text}
              autoFocus
              placeholder="What should it say?"
              onChange={(e) => setWords(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.currentTarget.parentElement!.querySelector('button') as HTMLButtonElement).click();
                } else if (e.key === 'Escape') {
                  setTextAt(null);
                  setEditingText(false);
                }
              }}
            />
            <button
              type="button"
              disabled={words.trim() === ''}
              onClick={() => {
                if (editingText && picked) void change(() => realms.saveDrawing(file, mapId, { ...inputOf(picked), text: words }), 'The text could not be saved.');
                else if (textAt) add('TEXT', [textAt], words);
                setTextAt(null);
                setEditingText(false);
              }}
            >
              {editingText ? 'Save' : 'Add'}
            </button>
          </div>
        )}
        <div className="actions">
          <button type="button" disabled={undo.length === 0} title="Undo the last drawing change (Ctrl+Z)" onClick={() => void undoOnce()}>
            ↶ Undo
          </button>
          <button type="button" disabled={redo.length === 0} title="Redo (Ctrl+Y)" onClick={() => void redoOnce()}>
            ↷ Redo
          </button>
          <button type="button" disabled={!picked} onClick={deleteSelected}>
            Delete
          </button>
          <button
            type="button"
            disabled={picked?.kind !== 'TEXT'}
            onClick={() => {
              setWords(picked?.text ?? '');
              setEditingText(true);
            }}
          >
            Edit text…
          </button>
        </div>
      </div>
    ),
  };
  return tool;
}

/** The picked drawing's bounds, dashed, so it's clear what the panel restyles. */
function SelectionBox({ drawing, offset }: { drawing: MapDrawing; offset: MapPoint | null }) {
  const [x0, y0, x1, y1] = drawingBounds(drawing);
  const dx = offset?.x ?? 0;
  const dy = offset?.y ?? 0;
  return <rect x={x0 + dx - 4} y={y0 + dy - 4} width={x1 - x0 + 8} height={y1 - y0 + 8} className="atlas-drawing-selected" vectorEffect="non-scaling-stroke" pointerEvents="none" />;
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en')} ${n === 1 ? one : many}`;
const round = (p: MapPoint): MapPoint => ({ x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 });
